import { Injectable, inject } from '@angular/core';
import { Observable, from, map, switchMap } from 'rxjs';
import { ApiService } from './api-base.service';
import {
    IDocumentResponse,
    ICreateDocumentDto,
    IUpdateDocumentDto,
    IDocumentFilters,
    IDocumentPaginationResponse,
    DocumentStatus,
    IAvailableUser,
    IDocumentClientEvidence,
} from '../../interfaces';
import { ApiPaginatedResponse } from '../../interfaces/api.interface';
import { DOCUMENT_ENDPOINTS, DOCUMENT_CACHE_KEYS } from '../../constants';
import { MobilePlatformService } from '../mobile/mobile-platform.service';

@Injectable({ providedIn: 'root' })
export class DocumentService {
    private readonly apiService = inject(ApiService);
    private readonly mobilePlatform = inject(MobilePlatformService);

    /**
     * Cria um novo documento
     */
    criar(dto: ICreateDocumentDto): Observable<IDocumentResponse> {
        return this.apiService.post<IDocumentResponse>(
            DOCUMENT_ENDPOINTS.BASE,
            dto,
            {
                useCache: false,
                showLoader: true,
            }
        );
    }

    /**
     * Lista usuários disponíveis para envio de documentos
     */
    listarUsuariosDisponiveis(search?: string, recipientType?: string): Observable<IAvailableUser[]> {
        if (!recipientType) {
            throw new Error('recipientType é obrigatório');
        }

        const params: Record<string, string> = {
            role: recipientType,
        };
        if (search) {
            params['search'] = search;
        }

        return this.apiService.get<IAvailableUser[]>(
            DOCUMENT_ENDPOINTS.RECIPIENTS,
            {
                params,
                cacheKey: DOCUMENT_CACHE_KEYS.RECIPIENTS(recipientType, search || ''),
            }
        );
    }

    /**
     * Lista documentos enviados pelo usuário
     */
    listarEnviados(page = 1, limit = 20, search = ''): Observable<IDocumentPaginationResponse> {
        const params: Record<string, string> = {
            page: page.toString(),
            limit: limit.toString(),
        };
        if (search.trim()) {
            params['search'] = search.trim();
        }
        const filters = `page-${page}-limit-${limit}-search-${search.trim()}`;

        return this.apiService.get<IDocumentPaginationResponse>(
            DOCUMENT_ENDPOINTS.SENT,
            {
                params,
                useCache: true,
                cacheKey: DOCUMENT_CACHE_KEYS.SENT(filters),
                cacheTtl: 30000, // 30 segundos
            }
        );
    }

    /**
     * Lista documentos recebidos pelo usuário
     */
    listarRecebidos(page = 1, limit = 20, search = ''): Observable<IDocumentPaginationResponse> {
        const params: Record<string, string> = {
            page: page.toString(),
            limit: limit.toString(),
        };
        if (search.trim()) {
            params['search'] = search.trim();
        }
        const filters = `page-${page}-limit-${limit}-search-${search.trim()}`;

        return this.apiService.get<IDocumentPaginationResponse>(
            DOCUMENT_ENDPOINTS.RECEIVED,
            {
                params,
                useCache: true,
                cacheKey: DOCUMENT_CACHE_KEYS.RECEIVED(filters),
                cacheTtl: 30000, // 30 segundos
            }
        );
    }

    /**
     * Busca um documento por ID
     */
    buscarPorId(id: string): Observable<IDocumentResponse> {
        return this.apiService.get<IDocumentResponse>(
            DOCUMENT_ENDPOINTS.BY_ID(id),
            {
                useCache: true,
                cacheKey: DOCUMENT_CACHE_KEYS.BY_ID(id),
                cacheTtl: 60000, // 1 minuto
            }
        );
    }

    /**
     * Atualiza o status de um documento e envia evidência do dispositivo
     * (prova jurídica — IP/UA/device ficam só no backend).
     */
    atualizarStatus(id: string, status: DocumentStatus): Observable<IDocumentResponse> {
        return from(this.collectClientEvidence()).pipe(
            switchMap((clientEvidence) =>
                this.apiService.patch<IDocumentResponse>(
                    DOCUMENT_ENDPOINTS.UPDATE_STATUS(id),
                    { status, clientEvidence },
                    {
                        useCache: false,
                        showLoader: true,
                    },
                ),
            ),
            map((response) => {
                this.invalidateCache(id);
                return (response as any)?.data ?? response;
            }),
        );
    }

    /**
     * Coleta metadados do dispositivo/navegador para auditoria jurídica.
     */
    private async collectClientEvidence(): Promise<IDocumentClientEvidence> {
        const platformInfo = this.mobilePlatform.getPlatformInfo();
        const device = await this.mobilePlatform.getDeviceInfo().catch(() => null);
        const nav = typeof navigator !== 'undefined' ? navigator : null;
        const screenInfo =
            typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : undefined;

        let browser: string | undefined;
        if (nav?.userAgent) {
            if (/Edg\//.test(nav.userAgent)) browser = 'Edge';
            else if (/Chrome\//.test(nav.userAgent)) browser = 'Chrome';
            else if (/Safari\//.test(nav.userAgent) && !/Chrome\//.test(nav.userAgent))
                browser = 'Safari';
            else if (/Firefox\//.test(nav.userAgent)) browser = 'Firefox';
        }

        return {
            userAgent: nav?.userAgent,
            platform: platformInfo.platform,
            isNative: platformInfo.isNative,
            appVersion: platformInfo.nativeVersion ?? undefined,
            deviceModel: device?.model,
            manufacturer: device?.manufacturer,
            os: device?.operatingSystem ?? undefined,
            osVersion: device?.osVersion,
            browser,
            screen: screenInfo,
            language: nav?.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
    }

    /**
     * Atualiza um documento
     */
    atualizar(id: string, dto: IUpdateDocumentDto): Observable<IDocumentResponse> {
        return this.apiService.patch<IDocumentResponse>(
            DOCUMENT_ENDPOINTS.BY_ID(id),
            dto,
            {
                useCache: false,
                showLoader: true,
            }
        ).pipe(
            map(() => {
                // Invalidar cache
                this.invalidateCache(id);
                return {} as IDocumentResponse;
            })
        );
    }

    /**
     * Remove um documento
     */
    remover(id: string): Observable<{ message: string }> {
        return this.apiService.delete<{ message: string }>(
            DOCUMENT_ENDPOINTS.BY_ID(id),
            {
                useCache: false,
                showLoader: true,
            }
        ).pipe(
            map(() => {
                // Invalidar cache
                this.invalidateCache(id);
                return { message: 'Documento removido com sucesso' };
            })
        );
    }

    /**
     * Invalida o cache de um documento
     */
    private invalidateCache(id?: string): void {
        if (id) {
            // Invalidar cache específico
            this.apiService.clearCacheByKey(DOCUMENT_CACHE_KEYS.BY_ID(id));
        }
        // Invalidar cache de listas
        this.apiService.clearCacheByPattern(DOCUMENT_CACHE_KEYS.PATTERN);
    }

    /**
     * Invalida todo o cache de documentos
     */
    invalidateAllCache(): void {
        this.apiService.clearCacheByPattern(DOCUMENT_CACHE_KEYS.PATTERN);
    }
}
